import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { forkJoin } from "rxjs";
import {
  AsReportedSecurityBreakdownTags,
  InsiderTradingPlans,
  InsiderTradingPlansFilters,
  IssuerTradingPlansFilters,
  IssuerWithInsiderTradingPlans,
} from "src/api/models";
import { IssuerTradingPlansService, InsiderViewTradingPlansService } from "src/api/services";
import {
  preventLeadingSpaceKey,
  handlePasteWithoutLeadingSpaces,
} from "../../../core/utils/input-validation.utils";
// interface PlansData {
//   issuer: string;
//   issuerId: number | string;
//   plans: InsiderTradingPlans[];
// }
@Component({
  selector: "app-issuer-details",
  templateUrl: "./issuer-details.component.html",
  styleUrls: ["./issuer-details.component.css"],
  standalone: false,
})
export class IssuerDetailsComponent implements OnInit {
  @ViewChild("scrollContainer") scrollContainer!: ElementRef;
  first = 0;
  rows = 10;
  isLoading: boolean = false;
  filingDateFrom: Date | null = null;
  filingDateTo: Date | null = null;
  issuerId: any;
  issuerName: any;
  insiderTradingPlans: InsiderTradingPlans[] = [];
  // plans: InsiderTradingPlans[] = [];
  readonly paginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };
  // plansData: PlansData[] = [];
  filteredPlans: any[] = [];
  isLastPage: boolean = false;
  selectedInsiders: any[] = [];
  selectedTags: any[] = [];
  insiderList: { insiderId?: number; insiderName?: string }[] = [];
  selectedInsiderNames: { insiderId?: number; insiderName?: string }[] = [];
  insiderDetailsData: InsiderTradingPlans[] = [];
  groupedInsiderDetails: Array<{ insiderId?: number; insiderName?: string; plans: InsiderTradingPlans[] }> = [];
  planActions = [
    { label: "All", value: "" },
    { label: "Adoption", value: "adoption" },
    { label: "Termination", value: "termination" },
    { label: "Amendment", value: "amendment" },
  ];

  // securityBreakDownTags: AsReportedSecurityBreakdownTags | null
  tags = [
    { label: "Sell to Cover", key: "sellToCover", disable: false },
    { label: "Award Related", key: "awardRelated", disable: false },
    { label: "Gift Related", key: "giftRelated", disable: false },
    { label: "Tax Withheld", key: "taxWithheld", disable: false },
    { label: "Remuneration", key: "remuneration", disable: false },
    { label: "Options Related", key: "optionsRelated", disable: false },
    { label: "Indeterminable", key: "isIndeterminable", disable: false },
  ];

  filters: any = {
    filingId: "",
    issuerName: "",
    insider: "",
    action: "",
    tag: "",
    planAction: "",
    indeterminable: false,
    filingDateFrom: null,
    filingDateTo: null,
    includeIndeterminable: false,
    filingInDuplicateQualityGate: false,
    tradingPlanInDuplicateQualityGate: false,
    hasModifiedFields: false,
  };
  readonly insiderDetailsPaginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };
  insiderDetailsFirst = 0;
  isFiltersActive(): boolean {
    const f = this.filters;
    return !!(
      f.filingId ||
      f.issuerName ||
      f.insider ||
      f.action ||
      f.tag ||
      f.filingDateFrom ||
      f.filingDateTo ||
      f.indeterminable ||
      f.includeIndeterminable ||
      f.planAction ||
      f.filingInDuplicateQualityGate ||
      f.tradingPlanInDuplicateQualityGate ||
      f.hasModifiedFields ||
      (this.selectedInsiderNames && this.selectedInsiderNames.length > 0)
    );
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public issuerTradingPlansService: IssuerTradingPlansService,
    public insiderViewTradingPlansService: InsiderViewTradingPlansService,
    public _messageService: MessageService
  ) {}
  normalizeFilters() {
    Object.keys(this.filters).forEach((key) => {
      if (typeof this.filters[key] === "string") {
        this.filters[key] = this.filters[key].trim();
      }
    });
  }
  ngOnInit(): void {
    this.issuerId = this.route.snapshot.queryParamMap.get("issuerId");
    this.issuerName = this.route.snapshot.queryParamMap.get("issuerName");
    if (this.issuerId) {
      console.log("issuerId", this.issuerId);
      this.fetchInsidersList();
      // this.fetchData(this.issuerId);
    }
  }

  fetchInsidersList(): void {
    if (!this.issuerId) return;
    
    // Fetch all insiders by getting all pages
    // We need ALL insiders regardless of pagination
    const uniqueInsidersMap = new Map<number, { insiderId?: number; insiderName?: string }>();
    const pageSize = 1000;
    let currentPage = 1;
    let totalFetched = 0;
    let totalCount = 0;
    const requests: any[] = [];

    // First, fetch first page to get total count
    const firstPageParams: IssuerTradingPlansFilters = {
      issuerId: this.issuerId,
      pageNumber: 1,
      pageSize: pageSize,
    };

    this.issuerTradingPlansService.getIssuerTradingPlans(firstPageParams).subscribe({
      next: (plans) => {
        // Extract insiders from first page
        (plans || []).forEach((plan) => {
          if (plan.insiderId && plan.insiderName) {
            if (!uniqueInsidersMap.has(plan.insiderId)) {
              uniqueInsidersMap.set(plan.insiderId, {
                insiderId: plan.insiderId,
                insiderName: plan.insiderName,
              });
            }
          }
        });

        totalFetched = plans?.length || 0;
        totalCount = plans && plans.length > 0 ? (plans[0].totalCount || 0) : 0;

        // If there are more pages, fetch them
        if (totalCount > totalFetched) {
          const remainingPages = Math.ceil((totalCount - totalFetched) / pageSize);
          
          for (let page = 2; page <= remainingPages + 1; page++) {
            const params: IssuerTradingPlansFilters = {
              issuerId: this.issuerId,
              pageNumber: page,
              pageSize: pageSize,
            };
            requests.push(this.issuerTradingPlansService.getIssuerTradingPlans(params));
          }

          // Fetch all remaining pages
          if (requests.length > 0) {
            forkJoin(requests).subscribe({
              next: (results) => {
                // Extract insiders from all pages
                results.forEach((pagePlans) => {
                  (pagePlans || []).forEach((plan:any) => {
                    if (plan.insiderId && plan.insiderName) {
                      if (!uniqueInsidersMap.has(plan.insiderId)) {
                        uniqueInsidersMap.set(plan.insiderId, {
                          insiderId: plan.insiderId,
                          insiderName: plan.insiderName,
                        });
                      }
                    }
                  });
                });

                // Convert Map to Array - this gives us ALL unique insiders
                this.insiderList = Array.from(uniqueInsidersMap.values());
              },
              error: (err) => {
                // Even if some pages fail, use what we have
                this.insiderList = Array.from(uniqueInsidersMap.values());
                this._messageService.add({
                  summary: "Some insiders may be missing due to fetch error",
                  severity: "warn",
                });
              },
            });
          } else {
            // No more pages, use what we have
            this.insiderList = Array.from(uniqueInsidersMap.values());
          }
        } else {
          // All data fetched in first page
          this.insiderList = Array.from(uniqueInsidersMap.values());
        }
      },
      error: (err) => {
        this._messageService.add({
          summary: err.error?.message || "Failed to fetch insiders",
          severity: "error",
        });
      },
    });
  }

  onInsiderSelectionChange(): void {
    if (this.selectedInsiderNames && this.selectedInsiderNames.length > 0) {
      this.fetchInsiderDetails();
    } else {
      this.insiderDetailsData = [];
      this.groupedInsiderDetails = [];
      this.insiderDetailsPaginator.totalCount = 0;
    }
  }

  fetchInsiderDetails(): void {
    if (!this.selectedInsiderNames || this.selectedInsiderNames.length === 0) {
      return;
    }

    this.normalizeFilters();
    this.isLoading = true;
    this.insiderDetailsPaginator.pageNumber = 1;
    this.insiderDetailsFirst = 0;

    // Fetch data for all selected insiders
    const insiderIds = this.selectedInsiderNames
      .map((insider) => insider.insiderId)
      .filter((id): id is number => id !== undefined && id !== null);

    if (insiderIds.length === 0) {
      this.isLoading = false;
      return;
    }

    // Fetch data for each insider and combine
    const requests = insiderIds.map((insiderId) => {
      const params: IssuerTradingPlansFilters = {
        planAction: this.filters.planAction || undefined,
        pageNumber: 1,
        pageSize: 1000, // Large page size to get all data
        filingId: this.filters.filingId || undefined,
        issuerId: this.issuerId,
        insiderId: insiderId,
        isIndeterminable: this.filters.includeIndeterminable || undefined,
        filingInDuplicateQualityGate:
          this.filters.filingInDuplicateQualityGate || undefined,
        tradingPlanInDuplicateQualityGate:
          this.filters.tradingPlanInDuplicateQualityGate || undefined,
        hasModifiedFields: this.filters.hasModifiedFields || undefined,
        filingDateFrom: this.filters.filingDateFrom
          ? this.formatDateForBackend(this.filters.filingDateFrom)
          : undefined,
        filingDateTo: this.filters.filingDateTo
          ? this.formatDateForBackend(this.filters.filingDateTo)
          : undefined,
      };

      return this.insiderViewTradingPlansService.getInsiderViewTradingPlans(params);
    });

    // Combine all requests
    forkJoin(requests).subscribe({
      next: (results) => {
        // Flatten all results into a single array
        this.insiderDetailsData = results.flat();
        // Group data by insiderId
        this.groupInsiderDetails();
        this.insiderDetailsPaginator.totalCount = this.insiderDetailsData.length;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this._messageService.add({
          summary: err.error?.message || "Failed to fetch insider details",
          severity: "error",
        });
      },
    });
  }

  groupInsiderDetails(): void {
    const groupedMap = new Map<number, { insiderId?: number; insiderName?: string; plans: InsiderTradingPlans[] }>();

    this.insiderDetailsData.forEach((plan) => {
      if (plan.insiderId) {
        if (!groupedMap.has(plan.insiderId)) {
          groupedMap.set(plan.insiderId, {
            insiderId: plan.insiderId,
            insiderName: plan.insiderName,
            plans: [],
          });
        }
        groupedMap.get(plan.insiderId)!.plans.push(plan);
      }
    });

    this.groupedInsiderDetails = Array.from(groupedMap.values());
  }
  getBadgeClass(action: string): string {
    switch (action) {
      case "adoption":
        return "badge badge-adoption";
      case "termination":
        return "badge badge-termination";
      case "amendment":
        return "badge badge-amendment";
      default:
        return "";
    }
  }
  onExecute() {
    console.log("Execute triggered with:", this.filters);
  }
  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    if (this.scrollContainer?.nativeElement) {
      this.scrollContainer.nativeElement.scrollTo({
        top: 0,
        behavior: "smooth", // smooth scroll effect
      });
    }
  }
  loadData(event?: any) {
    if (event.first > 0 && this.paginator.pageSize != event.rows) {
      this.paginator.pageNumber = 1;
      this.first = 0;
    } else {
      this.paginator.pageNumber = event.first / event.rows + 1;
      this.first = event.first;
    }
    this.paginator.pageSize = event.rows;

    this.getInsiderTradingPlans();
  }

  getInsiderTradingPlans(): void {
    this.normalizeFilters();
    const params: IssuerTradingPlansFilters = {
      planAction: this.filters.planAction || undefined,
      pageNumber: this.paginator.pageNumber,
      pageSize: this.paginator.pageSize,
      filingId: this.filters.filingId || undefined,
      issuerId: this.issuerId,
      isIndeterminable: this.filters.includeIndeterminable || undefined,
      filingInDuplicateQualityGate:
        this.filters.filingInDuplicateQualityGate || undefined,
      tradingPlanInDuplicateQualityGate:
        this.filters.tradingPlanInDuplicateQualityGate || undefined,
      hasModifiedFields: this.filters.hasModifiedFields || undefined,
      filingDateFrom: this.filters.filingDateFrom
        ? this.formatDateForBackend(this.filters.filingDateFrom)
        : undefined,
      filingDateTo: this.filters.filingDateTo
        ? this.formatDateForBackend(this.filters.filingDateTo)
        : undefined,
      insiderName: this.filters.insiderName || undefined,
    };
    this.isLoading = true;
    this.issuerTradingPlansService.getIssuerTradingPlans(params).subscribe({
      next: (value) => {
        this.insiderTradingPlans = value;
      },
      error: (err) => {
        this.isLoading = false;
        this._messageService.add({
          summary: err.error.message,
          severity: "error",
        });
      },
      complete: () => {
        this.paginator.totalCount =
          this.insiderTradingPlans.length > 0
            ? this.insiderTradingPlans[0].totalCount!
            : 0;
        this.isLoading = false;
      },
    });
  }

onSubmitFilters(): void {

  // ✅ Block API if FID has letters (allow empty)
  const fid = String(this.filters?.filingId ?? '').trim();
  if (fid && !/^\d+$/.test(fid)) {
    return;
  }

  this.paginator.pageNumber = 1;
  this.insiderTradingPlans = [];
  this.selectedInsiders = [];

  if (this.selectedInsiderNames && this.selectedInsiderNames.length > 0) {
    this.fetchInsiderDetails();
  } else {
    this.getInsiderTradingPlans();
  }
}

  clearFilters() {
    this.filters = {
      filingId: "",
      issuerName: "",
      insider: "",
      action: "",
      tag: "",
      indeterminable: false,
      filingDateFrom: null,
      filingDateTo: null,
      includeIndeterminable: false,
      filingInDuplicateQualityGate: false,
      tradingPlanInDuplicateQualityGate: false,
      hasModifiedFields: false,
    };
    this.selectedTags = [];
    this.selectedInsiderNames = [];
    this.insiderDetailsData = [];
    this.groupedInsiderDetails = [];
    this.onSubmitFilters();
  }

  // Format to backend expected: yyyy-MM-dd
  formatDateForBackend(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  onApplyTags() {
    if (!this.selectedInsiders.length) {
      this._messageService.add({
        severity: "warn",
        summary: "Please select at least one insider to apply tags.",
      });
      return;
    }

    // Initialize all tags as false
    const securityBreakDownTags: AsReportedSecurityBreakdownTags = {
      awardRelated: false,
      giftRelated: false,
      isIndeterminable: false,
      modified: false,
      optionsRelated: false,
      remuneration: false,
      sellToCover: false,
      taxWithheld: false,
    };

    // Set selected tags to true
    this.selectedTags.forEach((tag) => {
      if (tag.key in securityBreakDownTags) {
        (securityBreakDownTags as any)[tag.key] = true;
      }
    });

    const payload = {
      asReportedSecurityBreakdownIds: this.selectedInsiders.map(
        (i) => i.asReportedSecurityBreakdownId
      ),
      securityBreakdownTags: securityBreakDownTags,
    };

    console.log("Applying Tags with payload:", payload);

    this.isLoading = true;
    this.issuerTradingPlansService
      .updateSecurityBreakdownTags(payload)
      .subscribe({
        next: (response) => {
          if (response) {
            this._messageService.add({
              severity: "success",
              summary: "Tags applied successfully!",
            });
          } else {
            this._messageService.add({
              severity: "error",
              summary: "Failed to apply tags.",
            });
          }
        },
        error: (err) => {
          console.error("Error:", err);
          this._messageService.add({
            severity: "error",
            summary: "Something went wrong while applying tags.",
          });
          this.isLoading = false;
          this.clearFilters();
        },
        complete: () => {
          this.isLoading = false;
          this.clearFilters();
        },
      });
  }
  getStringForTransactionTags(securityBreakDownTags: string | null): string[] {
    if (!securityBreakDownTags) return [];

    const map: Record<string, string> = {
      awardRelated: "A",
      optionsRelated: "O",
      giftRelated: "G",
      taxWithheld: "T",
      remuneration: "R",
      isIndeterminable: "I",
      sellToCover: "S",
    };

    return securityBreakDownTags
      .split(",")
      .map((tag) => map[tag.trim()])
      .filter(Boolean); // remove undefined
  }
  onTagSelection() {
    // Reset all disables first
    this.tags.forEach((tag) => (tag.disable = false));

    // Handle disabling conflicting tags
    this.selectedTags.forEach((sel) => {
      if (sel.key === "remuneration" || sel.key === "giftRelated") {
        this.tags.forEach((tag) => {
          if (tag.key !== sel.key) tag.disable = true;
        });
        // Ensure only this tag remains selected
        this.selectedTags = [sel];
      } else if (sel.key === "optionsRelated") {
        this.tags.forEach((tag) => {
          if (tag.key === "awardRelated") tag.disable = true;
        });
        this.selectedTags = this.selectedTags.filter(
          (t) => t.key !== "awardRelated"
        );
      } else if (sel.key === "awardRelated") {
        this.tags.forEach((tag) => {
          if (tag.key === "optionsRelated") tag.disable = true;
        });
        this.selectedTags = this.selectedTags.filter(
          (t) => t.key !== "optionsRelated"
        );
      }
    });

    // Update filters.tag: empty string if no selection
    this.filters.tag =
      this.selectedTags.length > 0
        ? this.selectedTags.map((tag) => tag.key).join(",")
        : "";

    // Debug logs
    const disabledCount = this.tags.filter((tag) => tag.disable).length;
    console.log("Disabled tags count:", disabledCount);
    console.log("filters.tag:", this.filters.tag);
  }
  onSelectionChange(event: any) {
    // if selected tags more than 1 clear the tags
    if (this.selectedInsiders.length > 1) {
      this.selectedTags = [];
      this.tags.forEach((t) => (t.disable = false));
      this.filters.tag = "";
      return;
    }
    const allSelectedTags: Set<string> = new Set();

    this.selectedInsiders.forEach((row) => {
      if (row.asReportedSecurityBreakdownTags) {
        row.asReportedSecurityBreakdownTags
          .split(",")
          .map((t: string) => t.trim())
          .forEach((tag: any) => allSelectedTags.add(tag));
      }
    });

    this.selectedTags = this.tags.filter((tag) => allSelectedTags.has(tag.key));
    this.onTagSelection();
    console.log("Combined Selected Tags:", this.selectedTags);
  }
  navigateToInsider(plan: IssuerTradingPlansFilters): void {
    if (!plan?.insiderName) {
      return;
    }
    this.router.navigate(["insider-details"], {
      queryParams: {
        insiderName: plan.insiderName,
        insiderId: plan.insiderId,
        issuerId: this.issuerId,
        issuerName: this.issuerName,
      },
    });
  }
  preventSpaceKey(event: KeyboardEvent): void {
    preventLeadingSpaceKey(event);
  }

  onPaste(event: ClipboardEvent, field: 'filingId' | 'insiderName'): void {
    handlePasteWithoutLeadingSpaces(event, (newValue: string) => {
      this.filters[field] = newValue;
    });
  }

  loadInsiderDetailsData(event?: any): void {
    if (event.first > 0 && this.insiderDetailsPaginator.pageSize != event.rows) {
      this.insiderDetailsPaginator.pageNumber = 1;
      this.insiderDetailsFirst = 0;
    } else {
      this.insiderDetailsPaginator.pageNumber = event.first / event.rows + 1;
      this.insiderDetailsFirst = event.first;
    }
    this.insiderDetailsPaginator.pageSize = event.rows;
    // Data is already loaded, just update pagination
  }

  pageChangeInsiderDetails(event: any): void {
    this.insiderDetailsFirst = event.first;
    this.insiderDetailsPaginator.pageSize = event.rows;
  }

}
